import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import AudioPlayer, { formatTime } from '../components/AudioPlayer';
import StarRating from '../components/StarRating';

const EMOTION_OPTIONS = [
  'Happy', 'Sad', 'Energetic', 'Calm', 'Nostalgic',
  'Inspired', 'Melancholy', 'Euphoric', 'Tense', 'Peaceful'
];

export default function ProjectDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [userRatings, setUserRatings] = useState({});
  const [userRankings, setUserRankings] = useState({});
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeVersion, setActiveVersion] = useState(null);
  const [userVersionRatings, setUserVersionRatings] = useState({});
  const [expandedTracks, setExpandedTracks] = useState(new Set());
  const [comment, setComment] = useState('');
  const [emotion, setEmotion] = useState('');
  const [currentTime, setCurrentTime] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const loadProject = useCallback(async () => {
    try {
      // Fetch project
      const { data: proj, error: projErr } = await supabase
        .from('projects')
        .select('*')
        .eq('id', id)
        .single();
      if (projErr || !proj) { navigate('/'); return; }
      setProject(proj);

      // Fetch tracks with remixes and versions (using views for stats)
      const { data: tracksData } = await supabase
        .from('tracks')
        .select(`
          *,
          remixes:remixes_with_stats(
            *,
            versions:versions_with_stats(*)
          )
        `)
        .eq('project_id', id)
        .order('track_number');

      // Sort versions within each remix
      const sortedTracks = (tracksData || []).map(t => ({
        ...t,
        remixes: (t.remixes || [])
          .sort((a, b) => a.remix_number - b.remix_number)
          .map(r => ({
            ...r,
            versions: (r.versions || []).sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
          }))
      }));
      setTracks(sortedTracks);

      // Fetch user ratings
      const { data: ratings } = await supabase
        .from('ratings')
        .select('remix_id, score')
        .eq('user_id', user.id);
      const ratingsMap = {};
      (ratings || []).forEach(r => { ratingsMap[r.remix_id] = r.score; });
      setUserRatings(ratingsMap);

      // Fetch user version ratings
      const { data: vRatings } = await supabase
        .from('version_ratings')
        .select('version_id, score')
        .eq('user_id', user.id);
      const vRatingsMap = {};
      (vRatings || []).forEach(r => { vRatingsMap[r.version_id] = r.score; });
      setUserVersionRatings(vRatingsMap);

      // Fetch user rankings
      const { data: ranks } = await supabase
        .from('rankings')
        .select('remix_id, version_id, rank')
        .eq('user_id', user.id);
      const rankingsMap = {};
      (ranks || []).forEach(r => {
        if (!rankingsMap[r.remix_id]) rankingsMap[r.remix_id] = {};
        rankingsMap[r.remix_id][r.version_id] = r.rank;
      });
      setUserRankings(rankingsMap);

      // Collect all version IDs for feedback
      const versionIds = [];
      (sortedTracks || []).forEach(t => {
        (t.remixes || []).forEach(r => {
          (r.versions || []).forEach(v => versionIds.push(v.id));
        });
      });

      if (versionIds.length > 0) {
        const { data: fb } = await supabase
          .from('feedback_with_author')
          .select('*')
          .in('version_id', versionIds)
          .order('created_at', { ascending: false });
        setFeedback(fb || []);
      }

      // Set initial active version
      if (sortedTracks.length > 0) {
        const firstTrack = sortedTracks[0];
        if (firstTrack.remixes?.length > 0) {
          const firstRemix = firstTrack.remixes[0];
          if (firstRemix.versions?.length > 0) {
            setActiveVersion(prev => prev || firstRemix.versions[firstRemix.versions.length - 1].id);
          }
        }
      }
    } catch {
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [id, user?.id, navigate]);

  useEffect(() => { loadProject(); }, [loadProject]);

  const handleRate = async (remixId, score) => {
    try {
      const { data } = await supabase.rpc('upsert_rating', { p_remix_id: remixId, p_score: score });
      if (data) {
        setUserRatings(prev => ({ ...prev, [remixId]: data.userScore }));
        setTracks(prev => prev.map(t => ({
          ...t,
          remixes: t.remixes.map(r =>
            r.id === remixId ? { ...r, avg_rating: data.averageRating, rating_count: data.ratingCount } : r
          )
        })));
      }
    } catch {}
  };

  const handleRateVersion = async (versionId, score) => {
    try {
      const { data } = await supabase.rpc('upsert_version_rating', { p_version_id: versionId, p_score: score });
      if (data) {
        setUserVersionRatings(prev => ({ ...prev, [versionId]: data.userScore }));
        setTracks(prev => prev.map(t => ({
          ...t,
          remixes: t.remixes.map(r => ({
            ...r,
            versions: r.versions.map(v =>
              v.id === versionId ? { ...v, avg_rating: data.averageRating, rating_count: data.ratingCount } : v
            )
          }))
        })));
      }
    } catch {}
  };

  const handleRankVersion = async (remixId, versionId, newRank, totalVersions) => {
    const updated = { ...(userRankings[remixId] || {}) };

    if (newRank === 0) {
      delete updated[versionId];
    } else {
      for (const [vid, r] of Object.entries(updated)) {
        if (vid !== versionId && r === newRank) {
          const oldRank = updated[versionId];
          if (oldRank) {
            updated[vid] = oldRank;
          } else {
            delete updated[vid];
          }
          break;
        }
      }
      updated[versionId] = newRank;
    }

    setUserRankings(prev => ({ ...prev, [remixId]: updated }));

    const rankingsArray = Object.entries(updated)
      .filter(([, r]) => r > 0)
      .map(([version_id, rank]) => ({ version_id, rank }));

    try {
      await supabase.rpc('submit_rankings', {
        p_remix_id: remixId,
        p_rankings: JSON.stringify(rankingsArray)
      });
    } catch {}
  };

  const handleFeedback = async (e) => {
    e.preventDefault();
    if ((!comment.trim() && !emotion) || !activeVersion) return;
    setSubmitting(true);
    try {
      const { data, error } = await supabase
        .from('feedback')
        .insert({
          version_id: activeVersion,
          user_id: user.id,
          comment: comment.trim() || null,
          emotion: emotion || null,
          timestamp_seconds: currentTime > 0 ? currentTime : null
        })
        .select()
        .single();

      if (!error && data) {
        // Fetch with author info
        const { data: fb } = await supabase
          .from('feedback_with_author')
          .select('*')
          .eq('id', data.id)
          .single();
        if (fb) setFeedback(prev => [fb, ...prev]);
      }
      setComment('');
      setEmotion('');
    } catch {}
    setSubmitting(false);
  };

  const toggleExpandTrack = (trackId) => {
    setExpandedTracks(prev => {
      const next = new Set(prev);
      if (next.has(trackId)) next.delete(trackId);
      else next.add(trackId);
      return next;
    });
  };

  const getVersionLabel = () => {
    for (const track of tracks) {
      for (const remix of track.remixes || []) {
        const v = remix.versions?.find(v => v.id === activeVersion);
        if (v) return `${track.title} - ${remix.title} - ${v.version_label}`;
      }
    }
    return '';
  };

  if (loading) return <div className="container mt-24 text-secondary">Loading...</div>;
  if (!project) return null;

  const versionFeedback = feedback.filter(f => f.version_id === activeVersion);

  return (
    <div className="container mt-24">
      <button className="btn btn-secondary btn-sm mb-16" onClick={() => navigate('/')}>
        &larr; Back
      </button>

      <h1 className="mb-8" style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-highlighted)', letterSpacing: '-0.3px' }}>
        {project.title}
      </h1>
      {project.description && (
        <p className="text-secondary mb-16">{project.description}</p>
      )}

      <p className="text-sm text-secondary mb-16">
        Rate remixes with stars. When a remix has multiple versions, rank them to pick your favorite. Leave feedback and share what emotion each version evokes.
      </p>

      {tracks.length === 0 && (
        <div className="empty-state">
          <h3>No tracks yet</h3>
          <p className="text-secondary">Check back soon for new tracks</p>
        </div>
      )}

      {tracks.map(track => (
        <div key={track.id} className="track-section">
          <div className="track-header" onClick={() => toggleExpandTrack(track.id)} style={{ cursor: 'pointer' }}>
            <div>
              <span className="track-number">Song {track.track_number}</span>
              <span className="track-title">{track.title}</span>
              <span className="badge badge-accent" style={{ marginLeft: 8 }}>
                {track.remixes?.length || 0} remix{(track.remixes?.length || 0) !== 1 ? 'es' : ''}
              </span>
            </div>
          </div>

          {(track.remixes || []).map(remix => {
            const hasMultipleVersions = (remix.versions?.length || 0) > 1;
            const remixRankings = userRankings[remix.id] || {};

            return (
              <div key={remix.id} className="remix-section" style={{
                marginLeft: 16, marginTop: 12, padding: '12px 16px',
                background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)'
              }}>
                <div className="flex-between mb-8">
                  <div>
                    <span style={{ fontWeight: 600, color: 'var(--color-primary)' }}>
                      {remix.title}
                    </span>
                    <span className="text-sm text-secondary" style={{ marginLeft: 8 }}>
                      {remix.version_count} version{remix.version_count !== 1 ? 's' : ''}
                    </span>
                    {!hasMultipleVersions && remix.avg_rating != null && Number(remix.avg_rating) > 0 && (
                      <span className="badge badge-accent" style={{ marginLeft: 8 }}>
                        Avg: {remix.avg_rating} ({remix.rating_count})
                      </span>
                    )}
                  </div>
                  <div className="flex-center gap-12">
                    {!hasMultipleVersions && (
                      <StarRating
                        value={userRatings[remix.id] || 0}
                        onChange={(score) => handleRate(remix.id, score)}
                      />
                    )}
                  </div>
                </div>

                {hasMultipleVersions && (
                  <p className="text-sm text-secondary mb-8">
                    Rank the versions below (1 = best)
                  </p>
                )}

                {(remix.versions || []).map(version => (
                  <div key={version.id} className="version-card">
                    <div className="flex-center gap-8 mb-4" style={{ flexWrap: 'wrap' }}>
                      <span className="version-label">{version.version_label}</span>
                      {hasMultipleVersions && (
                        <select
                          className="rank-select"
                          value={remixRankings[version.id] || ''}
                          onChange={(e) => handleRankVersion(remix.id, version.id, parseInt(e.target.value) || 0, remix.versions.length)}
                        >
                          <option value="">Rank</option>
                          {remix.versions.map((_, i) => (
                            <option key={i + 1} value={i + 1}>#{i + 1}</option>
                          ))}
                        </select>
                      )}
                      <StarRating
                        value={userVersionRatings[version.id] || 0}
                        onChange={(score) => handleRateVersion(version.id, score)}
                      />
                      {version.avg_rating != null && Number(version.avg_rating) > 0 && (
                        <span className="badge badge-accent">
                          Avg: {version.avg_rating} ({version.rating_count})
                        </span>
                      )}
                    </div>
                    <div className="mb-4" style={{ textAlign: 'right' }}>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => setActiveVersion(version.id)}
                        style={activeVersion === version.id
                          ? { borderColor: 'var(--color-primary)', color: 'var(--color-primary)' }
                          : {}}
                      >
                        {activeVersion === version.id ? 'Showing feedback' : 'Show feedback'}
                      </button>
                    </div>
                    <AudioPlayer
                      filename={version.filename}
                      mediaType={version.media_type}
                      onTimeUpdate={activeVersion === version.id ? setCurrentTime : undefined}
                    />
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      ))}

      {activeVersion && (
        <div className="mt-24">
          <h2 className="mb-16">
            Feedback for {getVersionLabel()}
          </h2>

          <form onSubmit={handleFeedback} className="card">
            <div className="form-group">
              <label>What emotion did this track evoke?</label>
              <div className="emotion-picker" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                {EMOTION_OPTIONS.map(e => (
                  <button
                    key={e}
                    type="button"
                    className={`btn btn-sm ${emotion === e ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setEmotion(prev => prev === e ? '' : e)}
                  >
                    {e}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label>Leave feedback {currentTime > 0 && (
                <span className="badge badge-accent" style={{ marginLeft: 8 }}>
                  at {formatTime(currentTime)}
                </span>
              )}</label>
              <textarea
                className="input"
                value={comment}
                onChange={e => setComment(e.target.value)}
                placeholder="What do you think about this version?"
              />
            </div>
            <button className="btn btn-primary btn-sm" disabled={submitting || (!comment.trim() && !emotion)}>
              {submitting ? 'Sending...' : 'Submit Feedback'}
            </button>
          </form>

          <div className="feedback-list">
            {versionFeedback.length === 0 ? (
              <p className="text-secondary text-sm mt-16">No feedback yet. Be the first!</p>
            ) : (
              versionFeedback.map(fb => (
                <div key={fb.id} className="feedback-item">
                  <div className="feedback-header">
                    <span className="feedback-author">{fb.display_name}</span>
                    <span className="feedback-time">
                      {new Date(fb.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {fb.emotion && (
                    <span className="badge badge-accent" style={{ marginBottom: 4, display: 'inline-block' }}>
                      {fb.emotion}
                    </span>
                  )}
                  <div className="feedback-text">
                    {fb.timestamp_seconds != null && (
                      <span className="feedback-timestamp">
                        {formatTime(fb.timestamp_seconds)}
                      </span>
                    )}
                    {fb.comment}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
